import { Suspense, lazy } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@astryxdesign/core/Button';
import { Badge } from '@astryxdesign/core/Badge';
import { Icon } from '@astryxdesign/core/Icon';
import type { FilterNode } from '@qronstat/shared';
import type { QuickFilterKey } from '../../screens/types';
import { RowsSkeleton } from '../../widgets/skeletons';
import { AdaptivePanel } from './Adaptive';

const FilterPanelContent = lazy(() =>
  import('./FilterPanelContent').then((m) => ({ default: m.FilterPanelContent })),
);

export interface FilterValue {
  filters: FilterNode[];
  realOnly: boolean;
}

export function FilterPanel({
  screenId,
  scope,
  quickKeys,
  value,
  onApply,
  count,
  isOpen: open,
  onOpenChange: setOpen,
}: {
  screenId: string;
  scope?: readonly string[];
  quickKeys: QuickFilterKey[];
  value: FilterValue;
  onApply: (value: FilterValue) => void;
  count: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const intl = useIntl();
  return (
    <AdaptivePanel
      isOpen={open}
      onOpenChange={setOpen}
      label={intl.formatMessage({ id: 'filters.title' })}
      width={640}
      trigger={(toggle) => (
        <Button
          label={intl.formatMessage({ id: 'filters.title' })}
          size="sm"
          variant={count ? 'primary' : 'secondary'}
          icon={<Icon icon="funnel" />}
          isIconOnly={!count}
          endContent={count ? <Badge label={String(count)} variant="neutral" /> : undefined}
          {...(toggle ? { onClick: toggle } : {})}
        />
      )}
    >
      {open ? (
        <Suspense fallback={<RowsSkeleton rows={5} />}>
          <FilterPanelContent
            screenId={screenId}
            {...(scope ? { scope } : {})}
            quickKeys={quickKeys}
            value={value}
            onApply={onApply}
            setOpen={setOpen}
          />
        </Suspense>
      ) : null}
    </AdaptivePanel>
  );
}
